import { apiErrorMessage } from '@agos/api-client'
import { tt } from '@agos/shared'
import { ConfirmDialog, useToast } from '@agos/ui'
import { useState } from 'react'

import { ConsentRow } from './ConsentRow'
import { pushConsent } from './customer-directory'
import { removePushSubscriptions, removedCountOf } from './push-subscriptions'

/**
 * Push in the customer drawer, beside e-mail and SMS - `FRONTEND.md` §9.735, owner O104.
 *
 * **The operator**: *"ani v systéme zákazníci keď si otvorím zákazníka nevidím ich tam že zapnúť vypnúť"*. The
 * drawer printed *Push: N prihlásených zariadení - nastavuje si zákazník sám* - a count, where e-mail and SMS have a
 * switch. A subscription is the customer's own browser, so the switch can only ever go **off**: it removes every one
 * after a question (§10A.381), and **on** stays disabled with the sentence saying who can.
 *
 * The removal is asked before it happens because it cannot be undone from here - only the customer can switch push
 * back on, in their own browser. The toast says the server's count of what it removed, not the drawer's.
 */
export function PushConsentRow({
  customerId,
  devices,
  onRemoved,
}: {
  customerId: number
  devices: number | undefined
  /** Refetch the drawer, so the row reads off from the server's own count. */
  onRemoved: () => void
}) {
  const { toast } = useToast()
  const consent = pushConsent(customerId, devices)
  const [asking, setAsking] = useState(false)
  const [removing, setRemoving] = useState(false)

  const remove = () => {
    setRemoving(true)
    void removePushSubscriptions(customerId)
      .then((answer) => {
        const removed = removedCountOf(answer)
        toast({
          title: tt('Push odhlásené'),
          description: removed === null ? undefined : `${tt('Odhlásených zariadení')}: ${String(removed)}`,
        })
        onRemoved()
      })
      // §9.395 - a refused removal must not read as a done one: the row stays as it was and the server says why.
      .catch((caught: unknown) => {
        toast({
          variant: 'destructive',
          title: tt('Push sa nepodarilo odhlásiť'),
          description: apiErrorMessage(caught, tt('Server odhlásenie odmietol.')),
        })
      })
      .finally(() => {
        setRemoving(false)
        setAsking(false)
      })
  }

  return (
    <div className="space-y-1" data-push-devices={consent.devices}>
      <ConsentRow
        label={tt('Push notifikácie')}
        detail={`${tt('Prihlásených zariadení')}: ${String(consent.devices)}`}
        checked={consent.checked}
        disabled={!consent.mayTurnOff || removing}
        // Only ever an off: the row is disabled when there is nothing on, so `next` is false whenever this runs.
        onChange={(next) => {
          if (!next) setAsking(true)
        }}
        testId="consent-push"
      />
      <p className="text-[11px] text-muted-foreground" data-push-consent-note>
        {consent.note}
      </p>
      <ConfirmDialog
        open={asking}
        onClose={() => setAsking(false)}
        onConfirm={remove}
        loading={removing}
        variant="danger"
        title={tt('Odhlásiť push notifikácie?')}
        description={`${tt('Zákazník prestane dostávať push notifikácie na všetkých prihlásených zariadeniach')} (${String(consent.devices)}). ${tt('Znova si ich môže zapnúť len sám - vo svojom účte v časti Notifikácie.')}`}
        confirmLabel={tt('Odhlásiť')}
        cancelLabel={tt('Zrušiť')}
      />
    </div>
  )
}
